import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE =
  "https://customer-assets.emergentagent.com/job_ded0e787-ce73-4799-bf5f-3aa93c0f5b06/artifacts/ujrhxkhv_61A82EC6-6494-4F9F-B1A6-04552E7A8224.jpeg";
const WALL =
  "https://customer-assets.emergentagent.com/job_ded0e787-ce73-4799-bf5f-3aa93c0f5b06/artifacts/e0itwate_81247389-BE6F-4C43-BDB6-AB97BB569FF1.jpeg";
const GUITAR_BG =
  "https://images.unsplash.com/photo-1681572128448-134c7683825d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHx2aW50YWdlJTIwZWxlY3RyaWMlMjBndWl0YXIlMjBvbiUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc4MDE3MDgxM3ww&ixlib=rb-4.1.0&q=85";
const LIGHTS =
  "https://images.pexels.com/photos/5389617/pexels-photo-5389617.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FOCUS_TO_POSITION = {
  top: "center 22%",
  center: "center center",
  bottom: "center 78%",
};

const FALLBACK = [
  { id: "f1", image_url: STAGE, title: "Live · The Top Hat", focus: "top" },
  { id: "f2", image_url: WALL, title: "Backstage · Kalispell", focus: "top" },
  { id: "f3", image_url: GUITAR_BG, title: "The Strat", focus: "center" },
  { id: "f4", image_url: LIGHTS, title: "Soundcheck", focus: "center" },
];

export const Gallery = () => {
  const { data: items = [] } = useQuery({
    queryKey: ["gallery"],
    queryFn: async () => (await axios.get(`${API_BASE}/gallery`)).data ?? [],
  });

  const display = items.length > 0 ? items : FALLBACK;

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative bg-[#0A0A0A] py-24 md:py-32 px-6 md:px-16 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#DC2626]" />
            <span className="font-oswald text-xs uppercase tracking-[0.3em] text-[#DC2626]">
              From the Road
            </span>
          </div>
          <h2 className="font-anton uppercase text-white text-5xl md:text-6xl lg:text-7xl leading-[0.9]">
            The <span className="text-[#B91C1C]">Gallery</span>
          </h2>
        </motion.div>

        {/* Uniform square tiles — every photo crops to center, no awkward stretching */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {display.map((it, i) => (
            <motion.div
              key={it.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: (i % 8) * 0.06 }}
              data-testid={`gallery-item-${i}`}
              className="relative overflow-hidden group aspect-square bg-black grain-overlay"
            >
              <img
                src={it.image_url}
                alt={it.title || ""}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                style={{ objectPosition: FOCUS_TO_POSITION[it.focus] || FOCUS_TO_POSITION.top }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                <span className="font-oswald uppercase tracking-[0.18em] text-[10px] md:text-xs text-white/90 truncate pr-2">
                  {it.title || `Photo ${i + 1}`}
                </span>
                <span className="font-anton text-[#DC2626] text-base md:text-lg">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
